import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { isUserAuthenticatedState, useSetDefaultUser, useSetUser } from "recoils";
import { useRecoilValue } from "recoil";
import { getAuthenticatedUser } from "api";
import { setHttpClientAuthToken } from "utils";
import { useAmplitude } from "hooks";

export const useAuth0Authentication = () => {
  const { isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();
  const { trackEvent } = useAmplitude();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const setUser = useSetUser();
  const setDefaultUser = useSetDefaultUser();
  const isUserAuthenticated = useRecoilValue(isUserAuthenticatedState);

  const [isHookLoading, setIsHookLoading] = useState(true);
  const hasFetchedUserRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setDefaultUser();
      hasFetchedUserRef.current = false;
    }
  }, [isAuthenticated, setDefaultUser]);

  useEffect(() => {
    if (!isAuthenticated || hasFetchedUserRef.current) {
      return;
    }
    hasFetchedUserRef.current = true;

    getAccessTokenSilently()
      .then((accessToken) => {
        if (!accessToken) {
          throw Error("Auth0: No auth token provided!");
        }

        setHttpClientAuthToken(accessToken);

        return getAuthenticatedUser();
      })
      .then((user) => {
        window.sessionStorage.setItem("columnFilters", JSON.stringify([]));
        window.sessionStorage.setItem("user", JSON.stringify(user));
        window.sessionStorage.setItem(
          "date_format",
          JSON.stringify(user?.dateFormat ?? "MM-DD-YYYY"),
        );
        if (user.enabled) {
          setUser(user);
          trackEventRef.current("Logged In", "Login page");
        } else {
          hasFetchedUserRef.current = false;
          setDefaultUser();
          // eslint-disable-next-line no-alert
          window.alert("This user login does not exist. Please contact your system administrator.");
          logout({ returnTo: window.location.origin });
        }
      })
      .catch(() => {
        hasFetchedUserRef.current = false;
        setDefaultUser();
        setHttpClientAuthToken();
        window.localStorage.clear();
        window.sessionStorage.clear();
        logout({ returnTo: window.location.origin });
      });
  }, [isAuthenticated, getAccessTokenSilently, logout, setUser, setDefaultUser]);

  useEffect(() => {
    if (isAuthenticated && isUserAuthenticated && !isLoading) {
      setIsHookLoading(false);
    } else if (!isAuthenticated && !isAuthenticated && !isLoading) {
      setIsHookLoading(false);
    }
  }, [isAuthenticated, isUserAuthenticated, isLoading]);

  useEffect(() => {
    if (isAuthenticated && isUserAuthenticated) {
      const refreshInterval = setInterval(async () => {
        try {
          const newAccessToken = await getAccessTokenSilently();
          setHttpClientAuthToken(newAccessToken);
        } catch (error) {
          console.error(error)
        }
      }, 60000);

      return () => {
        clearInterval(refreshInterval);
      };
    }
    return undefined;
  }, [isAuthenticated, isUserAuthenticated, getAccessTokenSilently]);

  return {
    isUserAuthenticated,
    isLoading: isHookLoading,
  };
};

export const useAuth0SignOut = () => {
  const { logout } = useAuth0();
  const setDefaultUser = useSetDefaultUser();
  function clearStorageWithoutKey(keyToSave: string) {
    const items = { ...localStorage };
    localStorage.clear();
    localStorage.setItem(keyToSave, items[keyToSave]);
  }
  return useCallback(() => {
    setDefaultUser();
    setHttpClientAuthToken();
    clearStorageWithoutKey('TERMLY_API_CACHE');
    window.sessionStorage.clear();
    logout({
      returnTo: window.location.origin,
    });
  }, [setDefaultUser, logout]);
};

export const useAuth0SignIn = () => {
  const { loginWithRedirect } = useAuth0();

  return useCallback(() => loginWithRedirect(), [loginWithRedirect]);
};
